import * as React from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import useKBConnection from "../hooks/useKBConnection";

const ADMIN_API_BASE =
  (import.meta.env.VITE_ADMIN_API_URL as string)?.replace(/\/$/, "") ??
  "https://velaro-admin-staging.azurewebsites.net";

interface WidgetConfig {
  theme: string;
  position: "bottom-right" | "bottom-left";
  placeholder: string;
  buttonLabel: string;
  title: string;
  accentColor: string;
}

const DEFAULT_CONFIG: WidgetConfig = {
  theme: "light",
  position: "bottom-right",
  placeholder: "Search for help...",
  buttonLabel: "Help",
  title: "Knowledge Base",
  accentColor: "#4c75ab",
};

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);

  async function copy() {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ position: "relative" }}>
      <pre style={{
        background: "#1e293b", color: "#e2e8f0", borderRadius: 8,
        padding: "16px 20px", fontSize: 12, overflowX: "auto",
        margin: 0, lineHeight: 1.6, fontFamily: "monospace"
      }}>
        <code>{code}</code>
      </pre>
      <button
        onClick={copy}
        style={{
          position: "absolute", top: 10, right: 10,
          background: copied ? "#16a34a" : "rgba(255,255,255,0.1)",
          color: "white", border: "none", borderRadius: 4,
          padding: "4px 10px", cursor: "pointer", fontSize: 11
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function WidgetPreview({ config, siteId }: { config: WidgetConfig; siteId: number | null }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const isRight = config.position === "bottom-right";

  return (
    <div style={{
      background: "#f1f5f9", borderRadius: 8, padding: 24,
      minHeight: 280, position: "relative", overflow: "hidden"
    }}>
      <div style={{ color: "var(--color-gray-400)", fontSize: 12, marginBottom: 8 }}>Preview (simulated)</div>

      {open && (
        <div style={{
          position: "absolute",
          bottom: 80, [isRight ? "right" : "left"]: 20,
          width: 340, background: "white",
          borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
          overflow: "hidden"
        }}>
          <div style={{ background: config.accentColor, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ color: "white", fontWeight: 600, fontSize: 15 }}>{config.title}</span>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>×</button>
          </div>
          <div style={{ padding: 16 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={config.placeholder}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 6,
                border: "1px solid #e2e8f0", fontSize: 14, boxSizing: "border-box",
                outline: "none"
              }}
            />
            {query.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: "var(--color-gray-400)", marginBottom: 8 }}>Results will appear here</div>
                {["Getting Started Guide", "Return & Refund Policy", "Account Settings"].map((t) => (
                  <div key={t} style={{
                    padding: "10px 12px", borderRadius: 6, background: "#f8fafc",
                    marginBottom: 6, cursor: "pointer", fontSize: 13
                  }}>
                    <div style={{ fontWeight: 500, color: config.accentColor }}>{t}</div>
                    <div style={{ fontSize: 11, color: "var(--color-gray-400)", marginTop: 2 }}>
                      Sample summary for this article...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "absolute",
          bottom: 20, [isRight ? "right" : "left"]: 20,
          background: config.accentColor, color: "white",
          border: "none", borderRadius: 24, padding: "10px 20px",
          cursor: "pointer", fontWeight: 600, fontSize: 14,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
        }}
      >
        {config.buttonLabel}
      </button>
    </div>
  );
}

export default function KBWidgetEmbedCode() {
  const api = useApi();
  const { kbStatus: status } = useKBConnection();
  const [config, setConfig] = React.useState<WidgetConfig>(DEFAULT_CONFIG);
  const [siteId, setSiteId] = React.useState<number | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await api.admin.get("api/site/current");
        if (res.ok) {
          const data = await res.json();
          setSiteId(data.siteId ?? data.id ?? null);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const embedCode = `<!-- Velaro KB Search Widget -->
<script>
  window.__velaro_kb = {
    siteId: "${siteId ?? "YOUR_SITE_ID"}",
    apiBase: "${ADMIN_API_BASE}",
    theme: "${config.theme}",
    position: "${config.position}",
    placeholder: "${config.placeholder}",
    buttonLabel: "${config.buttonLabel}",
    title: "${config.title}",
    accentColor: "${config.accentColor}"
  };
</script>
<script src="${ADMIN_API_BASE}/kb-widget.js" async></script>`;

  const set = <K extends keyof WidgetConfig>(k: K, v: WidgetConfig[K]) =>
    setConfig((c) => ({ ...c, [k]: v }));

  if (!status?.ok) {
    return (
      <div style={{ padding: 32, color: "var(--color-gray-500)" }}>
        KB is not configured for this site. Set up your knowledge base first.
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 32px", maxWidth: 900 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>KB Search Widget</h1>
      <p style={{ margin: "6px 0 24px", color: "var(--color-gray-500)", fontSize: 14 }}>
        An embeddable floating search button that lets visitors search your knowledge base from any page on your site.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Configure</h2>

          {([
            ["title", "Widget Title", "text"],
            ["buttonLabel", "Button Label", "text"],
            ["placeholder", "Search Placeholder", "text"],
            ["accentColor", "Accent Color", "color"],
          ] as [keyof WidgetConfig, string, string][]).map(([k, label, type]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{label}</label>
              <input
                type={type}
                value={config[k] as string}
                onChange={(e) => set(k, e.target.value as any)}
                style={{
                  width: type === "color" ? 48 : "100%",
                  padding: type === "color" ? 2 : "7px 10px",
                  borderRadius: 5, border: "1px solid var(--color-gray-200)", fontSize: 13,
                  height: type === "color" ? 32 : "auto",
                  boxSizing: "border-box"
                }}
              />
            </div>
          ))}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Position</label>
            <div style={{ display: "flex", gap: 8 }}>
              {(["bottom-right", "bottom-left"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => set("position", p)}
                  style={{
                    padding: "6px 14px", borderRadius: 5,
                    border: "1px solid",
                    borderColor: config.position === p ? config.accentColor : "var(--color-gray-200)",
                    background: config.position === p ? config.accentColor : "white",
                    color: config.position === p ? "white" : "inherit",
                    cursor: "pointer", fontSize: 13
                  }}
                >
                  {p === "bottom-right" ? "Right" : "Left"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Preview</h2>
          <WidgetPreview config={config} siteId={siteId} />
        </div>
      </div>

      <div style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Embed Code</h2>
        <p style={{ fontSize: 13, color: "var(--color-gray-500)", marginBottom: 12 }}>
          Paste this snippet into the <code>&lt;head&gt;</code> or before the closing <code>&lt;/body&gt;</code> tag of any page.
        </p>
        <CodeBlock code={embedCode} />
      </div>

      <div style={{ marginTop: 24, background: "#f0f9ff", borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
        <strong>Deploy options:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: 20, color: "var(--color-gray-600)", lineHeight: 1.8 }}>
          <li>Paste the snippet on any page — the widget appears automatically</li>
          <li>Works on any site: WordPress, Squarespace, Shopify, custom HTML</li>
          <li>Opens a slide-out panel with live KB search (no page reload)</li>
          <li>Respects your KB's EnableBotResponse filter — shows only bot-ready articles</li>
          <li>Mobile-friendly and accessible (keyboard navigable)</li>
        </ul>
      </div>
    </div>
  );
}
